<template>
	<div class="amelipro-first-login">
		<AmeliproDialog
			v-model="isVisible"
			labelledby="first-login-modal-title"
			unique-id="first-login-dialog"
			@change="emitEvent"
			@confirm="emitEvent"
		>
			<template #header>
				<div class="d-flex font-italic">
					<h2
						id="first-login-modal-title"
						class="text-h4"
					>
						{{ locales.cardTitle }}
					</h2>
				</div>
			</template>

			<template #default>
				<div class="d-flex ap-blue darken-2 px-6 py-4 align-center">
					<AmeliproIcon
						bordered
						icon="mainCoeur"
						icon-bg-color="ap-blue darken-2"
						icon-color="white"
						size="61px"
					/>

					<span class="ml-2 ap-white--text fs-18">
						{{ locales.header }}
					</span>
				</div>

				<div class="ap-grey lighten-3 pa-6">
					<h3 class="w-100 mb-2 font-weight-semibold">
						{{ locales.advantagesTitle }}
					</h3>

					<ul class="d-flex flex-wrap list-style-none">
						<li
							v-for="(items, index) in locales.advantages"
							:key="index"
							class="d-flex px-1 align-center mb-1"
							:class="screenSize === 'xs' ? 'w-100' : 'w-50 '"
						>
							<AmeliproIcon
								class="mr-1"
								icon-color="ap-blue-darken-2"
								mdi-padding="0"
								medium
							>
								{{ mdiChevronRight }}
							</AmeliproIcon>

							<p class="ma-0 fs-14">
								{{ items }}
							</p>
						</li>
					</ul>
				</div>

				<div class="px-6 pt-6">
					<h3 class="mb-4 font-weight-semibold">
						{{ locales.servicesTitle }}
					</h3>

					<ul class="d-flex flex-wrap list-style-none">
						<li
							v-for="(items, index) in locales.services"
							:key="index"
							class="pb-4"
							:class="screenSize === 'xs' ? 'w-100' : 'w-50 pr-12'"
						>
							<div class="d-flex">
								<AmeliproIcon
									bordered
									class="mr-2"
									:icon="items.icon"
									icon-color="ap-blue darken-2"
									x-large
								/>

								<div>
									<h4 class="ap-blue--text text--darken-2 text-uppercase font-weight-semibold">
										{{ items.title }}
									</h4>

									<p class="ma-0 fs-12">
										{{ items.text }}
									</p>
								</div>
							</div>
						</li>
					</ul>
				</div>

				<div class="pa-6">
					<h3 class="font-weight-semibold">
						{{ locales.onlineProcessTitle }}
					</h3>

					<p class="fs-14">
						{{ locales.onlineProcessSubtitle }}
					</p>

					<ul class="d-flex flex-wrap list-style-none custom-marker">
						<li
							v-for="(items, index) in locales.onlineProcess"
							:key="index"
							class="d-flex"
							:class="screenSize === 'xs' ? 'w-100' : 'w-33'"
						>
							<p class="fs-12 ml-2 mb-1">
								{{ items }}
							</p>
						</li>
					</ul>
				</div>

				<div class="d-flex justify-center mb-7 mt-3">
					<p class="ap-blue lighten-3 rounded px-8 ma-0 py-2 fs-14 font-weight-semibold">
						Pour plus d’informations,
						<AmeliproBtn
							class="text-none font-weight-semibold btn-align"
							hover-underline
							:href="moreInfoHref"
							min-height="auto"
							text
							:to="moreInfoTo"
							@click="emitMoreInfoClickEvent"
						>
							cliquez ici
						</AmeliproBtn>
					</p>
				</div>
			</template>

			<template #footer>
				<div class="d-flex justify-end">
					<AmeliproBtn
						class="px-12"
						rounded
						@click="emitEvent(false)"
					>
						Continuer
					</AmeliproBtn>
				</div>
			</template>
		</AmeliproDialog>
	</div>
</template>

<script setup lang="ts">
	import { PropType, computed } from 'vue';
	import AmeliproBtn from '../AmeliproBtn/AmeliproBtn.vue';
	import AmeliproDialog from '../AmeliproDialog/AmeliproDialog.vue';
	import BreakpointHelper from '../../helpers/BreakpointHelper';
	import { RawLocation } from 'vue-router';
	import { locales } from './locales';
	import { mdiChevronRight } from '@mdi/js';

	const props = defineProps({
		moreInfoHref: {
			type: String,
			default: undefined,
		},
		moreInfoTo: {
			type: [Array, Object, String] as PropType<RawLocation>,
			default: undefined,
		},
		value: {
			type: Boolean,
			default: false,
		},
	});
	const emit = defineEmits(['input', 'change', 'click:more-info']);

	const isVisible = computed({
		get: (): boolean => props.value,
		set: (newValue: boolean): void => {
			emit('input', newValue);
		},
	});

	const breakpointHelper = new BreakpointHelper();
	const screenSize = breakpointHelper.name;

	const emitEvent = (value: boolean): void => {
		isVisible.value = value;
		emit('change', value);
	};

	const emitMoreInfoClickEvent = (): void => {
		emit('click:more-info');
	};
</script>

<style lang="scss" scoped>
.rounded {
	border-radius: 2.50rem !important;
}

.custom-sized-icon {
	width: 4em !important;
	height: 4em !important;
}

.w-50 {
	width: 50%;
	min-width: 50%;
}

.w-33 {
	width: calc((1 / 3) * 100%);
	min-width: calc((1 / 3) * 100%);
}

.fs-14 {
	font-size: $font-size-xs;
}

.fs-18 {
	font-size: $font-size-md;
}

.custom-marker {
	& li::before {
		content: "";
		display: block;
		width: 7px;
		height: 7px;
		margin-top: 7px;
		border-radius: 50%;
		background-color: $ap-blue-darken2;
	}
}

.btn-align {
	margin-top: -3px;
}
</style>
